import { useEffect } from 'react';

import { useGetContext } from '../api/lending/lapiHooks';

declare global {
  interface Window {
    Intercom?: ((...args: unknown[]) => void) & { q?: unknown[][] };
  }
}

const INTERCOM_SCRIPT_ID = 'intercom-script';
const INTERCOM_LAUNCHER_SELECTOR = '.ChatSupportButton';

/**
 * Loads and boots the Intercom widget using credentials from the site context.
 * Follows the official Intercom installation pattern:
 * https://www.intercom.com/help/en/articles/170-install-intercom-in-your-product-to-chat-with-logged-in-users
 *
 * Elements with the class "ChatSupportButton" will automatically open the messenger.
 */
export function useIntercom() {
  const { context } = useGetContext();

  const appId = context?.intercom_app_id;
  const userHash = context?.user_intercom_hash;

  useEffect(() => {
    if (!appId) return;

    if (typeof window.Intercom === 'function') {
      // Intercom already loaded (e.g. by Pandora, or a previous mount) — reattach
      window.Intercom('reattach_activator');
      window.Intercom('update', {
        custom_launcher_selector: INTERCOM_LAUNCHER_SELECTOR,
      });
    } else {
      // Set up the command queue so boot() is buffered until the script loads
      const queue: unknown[][] = [];
      const intercom = (...args: unknown[]) => {
        queue.push(args);
      };
      intercom.q = queue;
      window.Intercom = intercom;

      if (!document.getElementById(INTERCOM_SCRIPT_ID)) {
        const script = document.createElement('script');
        script.id = INTERCOM_SCRIPT_ID;
        script.async = true;
        script.src = `https://widget.intercom.io/widget/${appId}`;
        document.head.appendChild(script);
      }
    }

    window.Intercom('boot', {
      app_id: appId,
      user_hash: userHash,
      custom_launcher_selector: INTERCOM_LAUNCHER_SELECTOR,
    });
  }, [appId, userHash]);
}
